import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { useQuery } from '@tanstack/react-query';
import PageContainer from '@as_core/pages/PageContainer';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import { FlexColumn, FlexRow } from '@as_core/elements/flexStyles';
import SearchField from '@as_core/components/inputs/SearchField';
import { TextRegularBold, TextRegularItalics } from '@as_core/elements/textStyles';
import UnifiedButton from '@aseda_library/ui/buttons/UnifiedButton';
import DataLoading from '@as_core/elements/DataLoading';
import { UserContext } from '@as_core/contexts/UserContext';
import useHelpResources from '@as_core/pages/infoFaq/useHelpResources';
import HelpResourceTiles from '@as_core/pages/infoFaq/HelpResourcesTiles';
import SearchIcons from '@as_core/pages/infoFaq/SearchIcons';
import { getFeatureTagLabel, getProductLabel } from '@as_core/pages/infoFaq/ResourceTypeTiles/utils';
import { APP_CONFIG } from '@app_config/app';

function getSubAppName(subApp: string): string {
  switch (subApp) {
    case 'orders':
      return 'Orders';
    case '3rnd':
      return '3RnD';
    case 'chosmos':
      return 'ChOSMOS';
    case 'chemporium':
      return 'Chemporium';
    default:
      return subApp;
  }
}

const debug = true;
const PgHelp = () => {
  // get parameters from url query
  const location = useLocation();
  const navigate = useNavigate();
  const { subApp } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const queryFeature = queryParams.get('feature') ? queryParams.get('feature') : '';
  const queryText = queryParams.get('search') ? queryParams.get('search') : '';
  const subAppName = getSubAppName(subApp);

  const { getHelpResources } = useHelpResources();
  const [searchText, setSearchText] = useState<string>('');

  if (debug) console.log(`PgHelp | queryFeature: ${queryFeature} queryText: ${queryText} searchText: ${searchText}`);

  const runQuery = async() => {
    const params = {};
    if (queryFeature !== '') params['feature'] = queryFeature;
    if (searchText !== '') params['search'] = searchText;
    return await getHelpResources(params)
  }

  // DATA LOADING
  const { isPending, isError, error, data: helpResources } = useQuery({
    queryKey: ['helpResources', subApp, queryFeature, searchText],
    queryFn: () => runQuery()
  })
  if (isError) console.error(`PgHelp.searchHelpResources | Error ${error}`);
  if (debug) console.log(`PgHelp | isPending: ${isPending} helpResources:`, helpResources);

  const handleSearch = () => {
    let url = `/help/${subApp}/`
    let params = [];
    if (queryFeature) params.push(`feature=${queryFeature}`);
    if (searchText) params.push(`search=${searchText}`);
    if (params.length) {
      url = url + '?' + params.join('&');
    }
    navigate(url);
  };

  const onHandleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    if (searchText !== queryText) {
      setSearchText(queryText);
    }
  }, [queryText]);

  return (
    <PageContainer title={`Available Help Resources: ${subAppName}`} padding={0}>
      <FaqBody>
        <FlexRow width='100%' padding='10px 0 0' h_centered>
          <TextRegularBold color='accentSecondary'>
            Click on feature icon or search for available tutorials, videos, and FAQ help resources for {subAppName}.
          </TextRegularBold>
        </FlexRow>
        <FlexRow width='500px' padding='10px 0' h_centered v_centered>
          <TextRegularBold color='textSecondary'>Feature:</TextRegularBold>
          <SearchIcons subApp={subApp} queryFeature={queryFeature} />
        </FlexRow>
        <FlexRow width='500px' padding='10px 0' h_centered v_centered>
          <SearchField
            placeholder='Search help resources...'
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={onHandleEnter}
          />
          <UnifiedButton
            label='Search'
            width={80}
            height={32}
            onClick={handleSearch}
            disabled={!searchText || searchText.length < 4}
            />
        </FlexRow>
        <FlexRow width='100%' padding='10px 0' h_centered>
          {isPending ?
            <DataLoading />
          : helpResources && helpResources.length ?
            <FlexColumn width='100%' padding='10px 0' h_centered>
              { queryText !== '' && (
                <FlexRow width='100%' padding='0 0 24px 0' h_centered>
                  <TextRegularItalics>
                    Found {helpResources.length} item{helpResources.length > 1 ? 's' : ''} for search term &quot;{searchText}&quot;
                  </TextRegularItalics>
                </FlexRow>
                )}
              <HelpResourceTiles resources={helpResources} />
            </FlexColumn>
          : searchText !== '' && queryFeature !== '' ?
            <FlexRow width='100%' padding='0 0 24px 0' h_centered>
              <TextRegularItalics>
                No help resources found for search term &quot;
                {searchText}&quot; in feature &quot;{getFeatureTagLabel(queryFeature)}&quot;
              </TextRegularItalics>
            </FlexRow>
          : searchText === '' && queryFeature !== ''?
            <FlexRow width='100%' padding='0 0 24px 0' h_centered>
              <TextRegularItalics>
                No help resources available yet for feature &quot;{getFeatureTagLabel(queryFeature)}&quot;
              </TextRegularItalics>
            </FlexRow>
          :
            <FlexRow width='100%' padding='0 0 24px 0' h_centered>
              <TextRegularItalics>
                No help resources available yet for product &quot;{getProductLabel(APP_CONFIG.system, subApp)}&quot;
              </TextRegularItalics>
            </FlexRow>
          }
        </FlexRow>
      </FaqBody>
    </PageContainer>
  );
};

export default PgHelp;

const FaqBody = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
`;
